import { h } from "preact";
import { useRef, useState, useEffect } from "preact/hooks";
import { useDidMountEffect } from "../hooks";
import { clamp } from "../../foundation/utils/math";

import "./style.css";

type Offset = number | { top: number; right: number; bottom: number; left: number; };

interface Props {
    children: JSX.Element;
    x?: number;
    y?: number;
    offset?: Offset;
    onClick?: (ev: MouseEvent) => void;
}

export default function DraggablePanel({
    children,
    x = 0,
    y = 0,
    offset = 0,
    onClick
}: Props) {
    const panelRef = useRef<HTMLDivElement>(null);
    const [moving, setMoving] = useState(false);
    const [origin, setOrigin] = useState({ x: 0, y: 0 });
    const [delta, setDelta] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState({ x, y });
    const [visibility, setVisibility] = useState("hidden");

    // TODO: Obtain rects only once in useEffect?
    const {
        width: parentWidth = 0,
        height: parentHeight = 0
    } = panelRef.current?.parentElement?.getBoundingClientRect() ?? {};

    const { width = 0, height = 0 } = panelRef.current?.getBoundingClientRect() ?? {};
    let topOffset: number;
    let rightOffset: number;
    let bottomOffset: number;
    let leftOffset: number;

    if (typeof offset === "number") {
        topOffset = offset;
        rightOffset = offset;
        bottomOffset = offset;
        leftOffset = offset;
    } else {
        ({
            top: topOffset,
            right: rightOffset,
            bottom: bottomOffset,
            left: leftOffset
        } = offset);
    }

    useEffect(() => {
        if (moving) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        } else {
            document.removeEventListener("mouseup", handleMouseUp);
            document.removeEventListener("mousemove", handleMouseMove);
        }

        return () => {
            document.removeEventListener("mouseup", handleMouseUp);
            document.removeEventListener("mousemove", handleMouseMove);
        };
    }, [moving]);

    function getClampedPosition() {
        return {
            x: clamp(position.x + delta.x, leftOffset, parentWidth - width - rightOffset),
            y: clamp(position.y + delta.y, topOffset, parentHeight - height - bottomOffset)
        };
    }

    function handleMouseDown(ev: MouseEvent) {
        ev.preventDefault();
        ev.stopPropagation();

        setPosition(getClampedPosition());

        setOrigin({ x: ev.clientX, y: ev.clientY });
        setDelta({ x: 0, y: 0 });
        setMoving(true);
    }

    function handleMouseMove(ev: MouseEvent) {
        ev.preventDefault();
        ev.stopPropagation();

        setDelta({
            x: ev.clientX - origin.x,
            y: ev.clientY - origin.y
        });
    }

    function handleMouseUp(ev: MouseEvent) {
        ev.preventDefault();
        ev.stopPropagation();

        setMoving(false);
    }

    useDidMountEffect(() => setVisibility("visible"));

    const { x: translateX, y: translateY } = getClampedPosition();

    const style = {
        transform: `translate(${translateX}px, ${translateY}px)`,
        visibility
    };

    return (
        <div
            ref={panelRef}
            /* @ts-ignore */ // This one is fixed in next preact version(10.5.8) now it can not be able to resolve VisibilityProperty type
            style={style}
            class="draggableComponent"
            onMouseDown={handleMouseDown}
            onClick={onClick}>
            {children}
        </div>
    );
}
